@import '../../global/utility/responsive/responsive-mixins';
@import '~@union-investment/global-resources/src/styles/solid-design-system';

$sizes: 768, 0;
// used for the color replacement in the svg
$color-mappings: (
  '#00358e': $sd-backgroundColor-primary-DEFAULT,
  '#fff': $sd-backgroundColor-white,
  '#2d9d00': $sd-backgroundColor-accent-DEFAULT,
);
$max-number-points: 6;

:host {
  @include responsive($sizes);

  --ui-cms-value-chain-items: 3;
  --animation-duration-per-point: 2.2s;
  --animation-duration-half-point: calc(var(--animation-duration-per-point) / 2);
  --animation-duration-total: calc(var(--animation-duration-per-point) * var(--ui-cms-value-chain-items));

  display: block;
  width: 100%;

  @keyframes grow {
    from {
      opacity: 0;
      transform: scale(0);
    }

    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes fade-in {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  @keyframes draw-line {
    to {
      stroke-dashoffset: 0;
    }
  }

  @keyframes draw-short-line {
    to {
      stroke-dashoffset: var(--line-length);
    }
  }

  .valueChain {
    display: flex;
    flex-direction: row;
    gap: $sd-spacing-6;
    background-color: $sd-backgroundColor-primary-100;
    padding: $sd-spacing-6;

    &__itemWrapper {
      display: grid;
      grid-template-rows: repeat(var(--ui-cms-value-chain-items), 1fr);
      align-items: center;
      list-style: none;
    }

    .sd-interactive {
      font-weight: $sd-fontWeight-bold;
      text-decoration: underline;
    }

    .sd-headline {
      color: $sd-textColor-primary-DEFAULT;
      margin: 0 0 $sd-spacing-3 0;
    }

    &__infographic {
      .lineMask {
        stroke: $sd-textColor-white;
      }
    }

    &:not(.valueChain--animation) {
      .lineMask {
        stroke-dasharray: 0;
      }
    }

    &--animation {
      .valueChain__item,
      .lineStart,
      .lineEnd,
      .iconCircle,
      .pointCircle,
      .pointNumber {
        opacity: 0;
      }

      .iconContainer {
        svg {
          opacity: 0;
        }
      }

      &Triggered {
        .line {
          animation: draw-line 30s linear infinite; // infinite animation loop for the line
        }

        .lineMask {
          animation: draw-line var(--animation-duration-total) linear forwards; // mask is used to let the line travel from the start to the end at the beginning of the animation
        }

        .lineStart {
          animation: fade-in 0.6s forwards;
        }

        .lineEnd {
          animation: fade-in 0.6s forwards;
          animation-delay: var(--animation-duration-total);
        }

        .iconCircle {
          transform: scale(0);
          transform-box: fill-box;
          transform-origin: center;
          animation: grow 0.6s forwards;

          @for $i from 1 through $max-number-points {
            &--#{$i} {
              animation-delay: calc(var(--animation-duration-per-point) * #{$i} - var(--animation-duration-half-point));
            }
          }
        }

        .iconContainer {
          svg {
            animation: fade-in 0.6s forwards;

            @for $i from 1 through $max-number-points {
              &:nth-child(#{$i}) {
                animation-delay: calc(
                  var(--animation-duration-per-point) * #{$i} - var(--animation-duration-half-point) + 0.6s
                );
              }
            }
          }
        }

        .linePoint {
          stroke-dasharray: 40;
          stroke-dashoffset: 40;
          animation: draw-short-line 0.6s forwards;

          --line-length: 80; // double value to reverse the animation

          @for $i from 1 through $max-number-points {
            &--#{$i} {
              animation-delay: calc(
                var(--animation-duration-per-point) * #{$i} - var(--animation-duration-half-point) + 0.6s
              );
            }
          }
        }

        .pointCircle {
          transform: scale(0);
          transform-box: fill-box;
          transform-origin: center;
          animation-name: grow;
          animation-duration: 0.6s;
          animation-fill-mode: forwards;

          @for $i from 1 through $max-number-points {
            &--#{$i} {
              animation-delay: calc(
                var(--animation-duration-per-point) * #{$i} - var(--animation-duration-half-point) + 1s
              );
            }
          }
        }

        .pointNumber {
          animation: fade-in 0.6s forwards;

          @for $i from 1 through $max-number-points {
            &--#{$i} {
              animation-delay: calc(
                var(--animation-duration-per-point) * #{$i} - var(--animation-duration-half-point) + 1.2s
              );
            }
          }
        }

        .valueChain__item {
          animation: fade-in 0.6s forwards;

          @for $i from 1 through $max-number-points {
            &#{$i} {
              animation-delay: calc(
                var(--animation-duration-per-point) * #{$i} - var(--animation-duration-half-point) + 1.8s
              );
            }
          }
        }
      }
    }
  }

  svg {
    overflow: visible;
    display: flex;

    * {
      @each $color, $replacement-color in $color-mappings {
        &[fill='#{$color}' i] {
          fill: $replacement-color;
        }
        &[stroke='#{$color}' i] {
          stroke: $replacement-color;
        }
      }
    }
  }
}

@include min-width($sizes, 768) {
  .valueChain {
    flex-direction: column;
    gap: 0;

    &__itemWrapper {
      grid-template-rows: 1fr;
      grid-template-columns: repeat(var(--ui-cms-value-chain-items), 1fr);

      &:first-child {
        align-items: end;
      }

      &:last-child {
        align-items: start;
      }
    }

    &__infographic {
      margin: $sd-spacing-6 0;
    }

    &--animation {
      .linePoint {
        stroke-dasharray: 92;
        stroke-dashoffset: 92;

        --line-length: 184; // double value to reverse the animation
      }
    }
  }
}

.sr-only {
  @include sr-only;
}
